import { useForm } from '@tanstack/react-form';
import { useTranslation } from '../../../contexts/TranslationContext';
import { custom, maxValue, number, object, pipe } from 'valibot';
import { useEffect, useMemo } from 'react';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { useRecoilValue } from 'recoil';
import { MHMDistanceInputs } from '../MHM/MHMDistanceInputs';
import { Box } from '@mui/material';
import { useWarningLevel } from '../../../hooks/useWarningLevel';

const ftSchema = number('Please provide a valid number!');

type Props = {
  canEditSession: boolean;
  defaultWarningPercentage: number;
  onChange(
    percentage: number,
    errorMessage: [string, Record<string, string>],
  ): void;
};

export function DeviceWarningLevelFtIn({
  canEditSession,
  defaultWarningPercentage,
  onChange,
}: Props) {
  const { distance } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const { minMM, maxMM, toWarningDistance, toWarningPercentage } =
    useWarningLevel();

  function handleFeetInchesChange() {
    if (!selectedDevice) {
      return;
    }

    const feetValue = form.getFieldValue('warningFeet') || 0;

    // If inches has error but feet is valid, error text won't show because it doesn't re-render
    // So we need to update feet field to force re-render
    form.setFieldValue('warningFeet', feetValue);
  }

  const warningMM = toWarningDistance(defaultWarningPercentage);
  const rawInches = distance.from_metric(warningMM);

  const inSchema = useMemo(() => {
    return pipe(
      number('Please provide a valid number!'),
      maxValue(11, 'Inches must be less than 12'),
      custom(v => {
        const inchesValue = Number(v);
        const feetValue = Number(form.getFieldValue('warningFeet')) || 0;
        const totalMM = distance.to_metric(feetValue * 12 + inchesValue);
        return totalMM >= minMM && totalMM <= maxMM;
      }, 'Not in the allowable range feet/inches'),
    );
  }, [minMM, maxMM, distance]);

  const form = useForm({
    defaultValues: {
      warningFeet: Math.floor(rawInches / 12),
      warningInches: rawInches % 12,
    },
    validators: {
      onChange: object({ warningFeet: ftSchema, warningInches: inSchema }),
    },
    listeners: {
      onChangeDebounceMs: 1,
      onChange({ formApi }) {
        const ftValue = formApi.getFieldValue('warningFeet');
        const inValue = formApi.getFieldValue('warningInches');
        const warningDistanceMM = distance.to_metric(ftValue * 12 + inValue);
        const percentage = toWarningPercentage(warningDistanceMM);
        const errorMessage =
          (formApi.state.errorMap.onChange?.['warningFeet'][0].message ||
            formApi.state.errorMap.onChange?.['warningFeet'][0].message) ??
          '';
        onChange(percentage, [
          errorMessage,
          { minFtIn: distance.show(minMM), maxFtIn: distance.show(maxMM) },
        ]);
      },
    },
  });

  useEffect(() => {
    form.reset();
  }, [selectedDevice]);

  return (
    <Box display="grid" gridTemplateColumns="fit-content fit-content auto">
      <form.Field name="warningFeet">
        {field => {
          const value = Number.isNaN(field.state.value)
            ? ''
            : Math.round(field.state.value);
          const inchesField = form.getFieldMeta('warningInches');
          const hasErrors =
            !field.state.meta.isValid || (inchesField && !inchesField.isValid);

          return (
            <MHMDistanceInputs.Feet
              value={value}
              onChange={e => {
                field.handleChange(Number(e.target.value));
                handleFeetInchesChange();
              }}
              disabled={!canEditSession}
              error={hasErrors}
              slotProps={{ htmlInput: { sx: { minWidth: '3rem' } } }}
              sx={{ width: 'min-content', mr: 1 }}
              autoComplete="off"
            />
          );
        }}
      </form.Field>
      <form.Field
        name="warningInches"
        validators={{ onChangeListenTo: ['warningFeet'] }}
      >
        {field => {
          const value = Number.isNaN(field.state.value)
            ? ''
            : Math.round(field.state.value);
          const feetField = form.getFieldMeta('warningFeet');
          const hasErrors =
            !field.state.meta.isValid || (feetField && !feetField.isValid);

          return (
            <MHMDistanceInputs.Inches
              value={value}
              onChange={e => {
                field.handleChange(Number(e.target.value));
                handleFeetInchesChange();
              }}
              disabled={!canEditSession}
              error={hasErrors}
              slotProps={{ htmlInput: { sx: { minWidth: '3rem' } } }}
              sx={{
                width: 'min-content',
                gridRowStart: 1,
                gridColumnStart: 2,
              }}
              autoComplete="off"
            />
          );
        }}
      </form.Field>
    </Box>
  );
}
